import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Add } from '@mui/icons-material';

import { AdminRoles } from '../config/DiactiveTypes';
import { resolveUserDisplayNames } from '../utils/UserDisplayUtils';
import TutorialsTable from '../components/tutorials/TutorialsTable';
import TutorialFormDialog from '../components/tutorials/TutorialFormDialog';
import {
  fetchTutorials,
  TutorialAudience,
} from '../services/TutorialService';

const audienceTabs = [
  { label: 'Médicos', audience: TutorialAudience.Doctor },
  { label: 'Pacientes', audience: TutorialAudience.Patient },
];

const TutorialsAdmin = ({ role }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [userNamesById, setUserNamesById] = useState({});

  const audience = audienceTabs[tabIndex].audience;
  const showAppAccessColumn = audience === TutorialAudience.Patient;

  const loadTutorials = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTutorials({ audience });
      setItems(data);
      const userIds = data.flatMap((t) => [t.uploaderId, t.updatedById].filter(Boolean));
      const names = await resolveUserDisplayNames(userIds);
      setUserNamesById(names);
    }
    catch {
      setItems([]);
      setUserNamesById({});
      setSnackbar({ open: true, message: 'Error al cargar los tutoriales', severity: 'error' });
    }
    finally {
      setLoading(false);
    }
  }, [audience]);

  useEffect(() => {
    loadTutorials();
  }, [loadTutorials]);

  if (!AdminRoles.includes(role)) {
    return <Navigate to="/" replace />;
  }

  const handleOpenCreate = () => {
    setEditingTutorial(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (tutorial) => {
    setEditingTutorial(tutorial);
    setDialogOpen(true);
  };

  const handleSaved = () => {
    setSnackbar({ open: true, message: 'Tutorial guardado correctamente', severity: 'success' });
    loadTutorials();
  };

  const nextOrder = items.length > 0
    ? Math.max(...items.map((t) => t.order ?? 0)) + 1
    : 0;

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ mt: 10 }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Paper elevation={3} sx={{ padding: 3, pt: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ mb: 2, position: 'relative' }}
        >
          <Typography variant="h6" component="h1" sx={{ textAlign: 'center' }}>
            Gestión de tutoriales
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            disabled={loading}
            sx={{ position: 'absolute', right: 0 }}
          >
            Añadir tutorial
          </Button>
        </Stack>

        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          centered
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {audienceTabs.map((tab) => (
            <Tab key={tab.audience} label={tab.label} />
          ))}
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={45} />
          </Box>
        ) : (
          <TutorialsTable
            items={items}
            showAppAccessColumn={showAppAccessColumn}
            userNamesById={userNamesById}
            onEdit={handleOpenEdit}
            onRefresh={loadTutorials}
            onError={(message) =>
              setSnackbar({ open: true, message, severity: 'error' })
            }
          />
        )}
      </Paper>

      <TutorialFormDialog
        open={dialogOpen}
        mode={editingTutorial ? 'edit' : 'create'}
        audience={editingTutorial?.audience ?? audience}
        tutorial={editingTutorial}
        nextOrder={nextOrder}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />
    </Container>
  );
};

export default TutorialsAdmin;
